import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';

export type HistoryMode = 'push' | 'replace';

type QueryValues = object;
type EmptyMode = 'omit' | 'keep';

export type RouteQueryOptions<TValues extends QueryValues> = {
  parse?: (params: URLSearchParams) => Partial<TValues>;
  format?: (values: Partial<TValues>) => URLSearchParams | Record<string, unknown>;
  history?: HistoryMode;
  empty?: EmptyMode;
  scope?: Array<keyof TValues & string>;
};

type WriteOptions<TValues extends QueryValues> = Pick<
  RouteQueryOptions<TValues>,
  'format' | 'history' | 'empty'
>;

type ParameterReader<TValues extends QueryValues> = {
  <TKey extends keyof TValues & string>(name: TKey): Readonly<{ current: TValues[TKey] | null }>;

  <TValue>(
    name: string,
    options: {
      parse: (value: string | readonly string[]) => TValue;
    }
  ): Readonly<{ current: TValue | null }>;
};

export type RouteQueryController<TValues extends QueryValues> = Readonly<{
  current: Readonly<Partial<TValues>>;
  change: (values: Partial<TValues>, options?: WriteOptions<TValues>) => void;
  replace: (values: Partial<TValues>, options?: WriteOptions<TValues>) => void;
  clear: (options?: { history?: HistoryMode }) => void;
  parameter: ParameterReader<TValues>;
  raw: Readonly<{
    current: string;
    replace: (value: string, options?: { history?: HistoryMode }) => void;
  }>;
}>;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0 || value.every(isEmpty);
  if (isPlainObject(value)) return Object.values(value).every(isEmpty);

  return false;
}

function parseValue(value: string): unknown {
  const trimmed = value.trim();

  if (!trimmed.startsWith('[')) return value;

  try {
    const parsed: unknown = JSON.parse(trimmed);

    if (!Array.isArray(parsed)) return value;

    return parsed.map((item) => (typeof item === 'string' ? item : JSON.stringify(item)));
  } catch {
    return value;
  }
}

function pickValues<TValues extends QueryValues>(
  values: Partial<TValues>,
  scope: Array<keyof TValues & string>
): Partial<TValues> {
  const result: Partial<TValues> = {};

  for (const key of scope) {
    if (Object.prototype.hasOwnProperty.call(values, key)) {
      result[key] = values[key];
    }
  }

  return result;
}

function parseDefault(params: URLSearchParams, scope?: readonly string[]): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const keysToRead = scope ?? Array.from(new Set(params.keys()));

  for (const key of keysToRead) {
    const values = params.getAll(key);

    if (values.length === 0) continue;

    result[key] = values.length === 1 ? parseValue(values[0]) : values.map(parseValue);
  }

  return result;
}

function appendValue(params: URLSearchParams, key: string, value: unknown, empty: EmptyMode) {
  if (empty === 'omit' && isEmpty(value)) return;

  if (value === null || value === undefined) {
    params.append(key, '');
  } else if (Array.isArray(value) || isPlainObject(value)) {
    params.append(key, JSON.stringify(value));
  } else {
    params.append(key, String(value));
  }
}

export function serializeUrlQueryParameters<TValues extends QueryValues>(
  values: Partial<TValues>,
  format: RouteQueryOptions<TValues>['format'],
  empty: EmptyMode
) {
  const formatted = format?.(values) ?? values;
  const params = new URLSearchParams();
  const keys = new Set<string>();

  if (formatted instanceof URLSearchParams) {
    for (const [key, value] of formatted) {
      keys.add(key);
      appendValue(params, key, value, empty);
    }
  } else {
    for (const [key, value] of Object.entries(formatted)) {
      keys.add(key);
      appendValue(params, key, value, empty);
    }
  }

  return { params, keys };
}

// // URL: /path?x=[1,2,3]&page=1#details
// query.current;
// // { x: ['1', '2', '3'], page: '1' }
// query.change({ page: 2 });
// // /path?x=[1,2,3]&page=2#details
// query.replace({ page: 3 });
// // /path?page=3#details
// query.parameter('page', {
//   parse: (value) => Number(value),
// }).current;
// // 1
// query.raw.current;
// // "?x=[1,2,3]&page=1"
// query.raw.replace('?enabled=true');
export function useUrlQueryParameterSync<TValues extends QueryValues>(
  options: RouteQueryOptions<TValues> = {}
): RouteQueryController<TValues> {
  const { parse, format, history = 'push', empty = 'omit', scope } = options;

  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const current = useMemo(() => {
    const parsed = parse
      ? parse(new URLSearchParams(params))
      : (parseDefault(params, scope) as Partial<TValues>);

    return Object.freeze(scope ? pickValues(parsed, scope) : parsed);
  }, [params, parse, scope]);

  const commit = useCallback(
    (search: string, mode: HistoryMode) => {
      const normalized = search ? (search.startsWith('?') ? search : `?${search}`) : '';

      if (normalized === location.search) return;

      navigate(
        {
          pathname: location.pathname,
          search: normalized,
          hash: location.hash,
        },
        {
          replace: mode === 'replace',
          state: location.state,
        }
      );
    },
    [location, navigate]
  );

  const change = useCallback(
    (values: Partial<TValues>, overrides: WriteOptions<TValues> = {}) => {
      const valuesToWrite = scope ? pickValues(values, scope) : values;

      if (scope && Object.keys(valuesToWrite).length === 0) return;

      const serialized = serializeUrlQueryParameters(
        valuesToWrite,
        overrides.format ?? format,
        overrides.empty ?? empty
      );

      const next = new URLSearchParams(params);
      const keysToDelete = scope ? new Set<string>([...scope, ...serialized.keys]) : serialized.keys;

      for (const key of keysToDelete) next.delete(key);
      for (const [key, value] of serialized.params) next.append(key, value);

      commit(next.toString(), overrides.history ?? history);
    },
    [commit, format, history, empty, params, scope]
  );

  const replace = useCallback(
    (values: Partial<TValues>, overrides: WriteOptions<TValues> = {}) => {
      const valuesToWrite = scope ? pickValues(values, scope) : values;
      const serialized = serializeUrlQueryParameters(
        valuesToWrite,
        overrides.format ?? format,
        overrides.empty ?? empty
      );

      if (!scope) {
        commit(serialized.params.toString(), overrides.history ?? history);
        return;
      }

      const next = new URLSearchParams(params);

      for (const key of scope) next.delete(key);
      for (const [key, value] of serialized.params) next.append(key, value);

      commit(next.toString(), overrides.history ?? history);
    },
    [commit, format, history, empty, params, scope]
  );

  const clear = useCallback(
    (overrides: { history?: HistoryMode } = {}) => {
      if (!scope) {
        commit('', overrides.history ?? history);
        return;
      }

      const next = new URLSearchParams(params);

      for (const key of scope) next.delete(key);

      commit(next.toString(), overrides.history ?? history);
    },
    [commit, history, params, scope]
  );

  const parameter = useCallback(
    ((name: string, parameterOptions?: { parse?: (value: string | readonly string[]) => unknown }) => {
      if (parameterOptions?.parse) {
        const values = params.getAll(name);

        return Object.freeze({
          current:
            values.length === 0 ? null : parameterOptions.parse(values.length === 1 ? values[0] : values),
        });
      }

      return Object.freeze({
        current: Object.prototype.hasOwnProperty.call(current, name)
          ? ((current as Record<string, unknown>)[name] ?? null)
          : null,
      });
    }) as ParameterReader<TValues>,
    [current, params]
  );

  const rawReplace = useCallback(
    (value: string, overrides: { history?: HistoryMode } = {}) => {
      if (value.includes('#') || value.includes('/')) {
        throw new TypeError('query.raw.replace() only accepts a query string');
      }

      const search = value.startsWith('?') ? value.slice(1) : value;

      commit(search, overrides.history ?? history);
    },
    [commit, history]
  );

  return useMemo(
    () =>
      Object.freeze({
        current,
        change,
        replace,
        clear,
        parameter,
        raw: Object.freeze({
          current: location.search,
          replace: rawReplace,
        }),
      }),
    [change, clear, current, location.search, parameter, rawReplace, replace]
  );
}
